import { YaraMatch, YaraRulesetMatch } from "../../../../@types";
import { YaraMatchesGridProps } from "../../@types";

const classificationSortPriority: Record<string, number> = {
  malicious: -2,
  suspicious: -1
};
/**
 * Sorts the YaraMatchesGridProps based on the following criteria:
 * 1. Malicious rulesets first
 * 2. Suspicious rulesets second
 * 3. No of matches in descending order
 * @param a
 * @param b
 */
const sortYaraMatches = (a: YaraMatchesGridProps, b: YaraMatchesGridProps) => {
  const classificationPriority =
    (classificationSortPriority[a.matchedRulesetClassification ?? "unknown"] ||
      0) -
    (classificationSortPriority[b.matchedRulesetClassification ?? "unknown"] ||
      0);
  if (classificationPriority !== 0) return classificationPriority;

  return b.noOfMatches - a.noOfMatches;
};

/**
 * Returns the classification of the matched ruleset based on the rules
 *
 * If any rule is classified as malicious, the ruleset is classified as malicious.
 * If no rule is classified as malicious, but any rule is classified as suspicious, the ruleset is classified as suspicious.
 * If no rule is classified as malicious or suspicious, the ruleset is classified as undefined.
 * @param ruleset
 */
const getMatchedRulesetClassification = (ruleset: YaraRulesetMatch) =>
  ruleset.rules.reduce((acc, rule) => {
    if (rule.classname === "malicious") {
      return "malicious";
    }
    if (rule.classname === "suspicious" && acc !== "malicious") {
      return "suspicious";
    }
    return acc;
  }, undefined);

const getNumberOfYaraMatches = (yaraMatches?: Array<YaraMatch>) =>
  yaraMatches?.reduce((acc, yaraMatch) => acc + yaraMatch.rulesets.length, 0) ??
  0;

const convertYaraMatchesToGridData = (
  yaraMatches: Array<YaraMatch>
): Array<YaraMatchesGridProps> => {
  const gridRows: Array<YaraMatchesGridProps> = [];
  yaraMatches.forEach(yaraMatch => {
    yaraMatch.rulesets.forEach(ruleset => {
      const sumOfMatchedStrings = ruleset.rules.reduce(
        (acc, rule) => acc + rule.string_match_count,
        0
      );
      const matchedRulesetClassification =
        getMatchedRulesetClassification(ruleset);

      gridRows.push({
        matchedRulesetClassification,
        matchedRulesetName: ruleset.name,
        matchedRulesetId: ruleset.revision_id,
        matchedRules: ruleset.rules,
        noOfMatches: sumOfMatchedStrings,
        filename: yaraMatch.filename,
        fileClassification: yaraMatch.classification,
        fileSha1: yaraMatch.sha1,
        fileRelationshipId: yaraMatch.relationship_id
      });
    });
  });

  gridRows.sort(sortYaraMatches);

  return gridRows.slice(0, 5);
};

export { convertYaraMatchesToGridData, getNumberOfYaraMatches };
